import TextInput from "../../../../shared/components/TextInput/TextInput";
import { useAuth } from "../../../../shared/providers/AuthProvider/AuthProvider";
import {
    Box,
    Button,
    Flex,
    Link,
    Text,
    Stack,
} from '@chakra-ui/react';
import * as Yup from 'yup';
import { Formik, Form } from 'formik';
import { Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import FormErrorResetEffect from "./FormErrorResetEffect";
import InlineErrorInput from "../../../../shared/components/InlineErrorInput/InlineErrorInput";

const LoginPage = () => {
    const { login, user, error, errorCode, resetError } = useAuth();
    const navigate = useNavigate();
    const { t } = useTranslation("default");

    useEffect(() => {
        return () => {
            resetError();
        };
    }, []);

    if (user) {
        return <Navigate to={"/"} replace />
    }

    const handleSubmit = (values, { setSubmitting }) => {
        login(values.email, values.password);
        setSubmitting(false);
    }

    const handleNavigate = (route) => {
        navigate(route);
    }

    const validationSchema = Yup.object({
        email: Yup.string()
            .email(t('form.email.message'))
            .required(t('form.email.required')),
        password: Yup.string()
            .min(8, t('form.password.length'))
            .required(t('form.password.required')),
    });

    return (
        <Flex
            bgColor="#F3F3F3"
            bgSize="cover" bgPosition="center"
            w="100%" minH="100vh"
            align="center" justify="center"
            px={{ base: 4, sm: 6 }}>

            <Box w="full" align="center" display="flex" gap={'48px'} flexDirection={"column"} alignItems="center">
                <Stack spacing={6} align="center" mb={8}>
                    <Text className="login-title">
                        {t('login.title')}
                    </Text>
                    <Text className="login-message"
                        lineHeight={{ base: '16px', sm: 'sm' }}
                        fontSize={{ base: 20, sm: 24 }}
                    >
                        {t('login.message')}
                    </Text>
                </Stack>

                <Box maxW="3xl" w="full" bg="transparent" gap={'48px'}>
                    <Formik
                        initialValues={{ email: '', password: '' }}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                        validateOnChange={true}
                        validateOnBlur={true}
                    >
                        {({ isSubmitting, errors, touched, values, isValid, dirty }) => (
                            <Form>
                                <FormErrorResetEffect resetError={resetError} />
                                <Flex flexDirection={"column"} gap={'16px'}>
                                    <InlineErrorInput
                                        label={t('form.email.label')}
                                        name="email"
                                        type="email"
                                        placeholder={t('form.email.placeholder')}
                                        value={values.email}
                                        errors={errors.email}
                                        touched={touched.email}
                                        backgroundColor="white"
                                    />
                                    <Flex flexDirection={"column"} gap={'4px'}>
                                        <InlineErrorInput
                                            label={t('form.password.label')}
                                            name="password"
                                            type="password"
                                            placeholder={t('form.password.placeholder')}
                                            value={values.password}
                                            errors={errors.password}
                                            touched={touched.password}
                                            backgroundColor="white"
                                        />

                                        <Box textAlign="right">
                                            <Link
                                                className="login-forgot"
                                                href="/reset-password"
                                                color="#089A7A"
                                                _hover={{ textDecoration: "underline" }}
                                            >
                                                {t('login.forgot')}
                                            </Link>
                                        </Box>
                                    </Flex>
                                </Flex>

                                {errorCode && (
                                    <Text color="red.500" textAlign="center" mt={4}>
                                        {t(errorCode)}
                                    </Text>
                                )
                                    ||
                                    error && (
                                        <Text color="red.500" textAlign="center" mt={4}>
                                            {t(error)}
                                        </Text>
                                    )}

                                <Box display="flex" flexDirection="column" alignItems="center" mt={8}>
                                    <Button
                                        className="login-button"
                                        display={"flex"}
                                        type="submit"
                                        justifySelf="center"
                                        isLoading={isSubmitting && !error}
                                        gap={'10px'}
                                        bg="#089A7A"
                                        _hover={{ bg: "#1A7743" }}
                                        h={'59px'}
                                        w={'170px'}
                                        borderRadius={'4px'}
                                        isDisabled={isSubmitting || !isValid || !dirty}
                                    >
                                        <Text className="login-button-label">
                                            {t('login.button')}
                                        </Text>
                                        <svg width="8" height="10" viewBox="0 0 8 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M0 0H3.58481L8 5L3.58481 10H0L4.43544 5L0 0Z" fill="white" />
                                        </svg>
                                    </Button>
                                </Box>
                            </Form>
                        )}
                    </Formik>
                </Box>
                <Text className="login-register-message">
                    {t('login.register.message')}
                    <a href="/register" className="login-register-button">
                        {t('login.register.button')}
                    </a>
                </Text>
            </Box>
        </Flex>
    )
}

export default LoginPage;
